import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PORTFOLIO, SITE } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

function ProjectRow({ project, index }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: index * 0.06, ease: EASE }}
            data-testid={`project-card-${project.id}`}
            className={`group grid gap-3 border-b border-[#E5E1D8]/80 py-7 transition-colors duration-300 hover:bg-[#F4F0E8]/50 sm:gap-6 sm:px-4 ${
                project.image
                    ? "sm:grid-cols-[3.5rem_1fr_10rem]"
                    : "sm:grid-cols-[3.5rem_1fr]"
            }`}
        >
            <span className="font-mono text-xs text-[#8C8B88] transition-colors duration-300 group-hover:text-[#C84B31]">
                {String(index + 1).padStart(2, "0")}
            </span>
            {project.image && (
                <div className="h-40 w-full overflow-hidden rounded-lg border border-[#E5E1D8] sm:order-last sm:h-28 sm:w-40">
                    <img
                        src={project.image}
                        alt={project.title}
                        data-testid={`project-image-${project.id}`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                </div>
            )}
            <div>
                <h4 className="flex items-center gap-1.5 font-serif text-xl tracking-tight text-[#141413] transition-transform duration-300 group-hover:translate-x-1 sm:text-2xl">
                    {project.title}
                    <ArrowUpRight
                        size={17}
                        strokeWidth={1.75}
                        className="text-[#C84B31] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                </h4>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#6E6D6A] sm:text-[15px]">
                    {project.summary}
                </p>
                <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
                    {project.meta.map((t) => (
                        <li
                            key={t}
                            className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C8B88]"
                        >
                            {t}
                        </li>
                    ))}
                </ul>
            </div>
        </motion.article>
    );
}

function Category({ category, offset }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7, ease: EASE }}
        >
            <div className="mt-16 flex flex-wrap items-baseline justify-between gap-2 first:mt-0">
                <h3 className="font-serif text-2xl italic tracking-tight text-[#141413] sm:text-3xl">
                    {category.name}
                </h3>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8C8B88]">
                    {category.blurb}
                </p>
            </div>
            <div className="mt-4 border-t border-[#E5E1D8]/80">
                {category.projects.map((p, i) => (
                    <ProjectRow key={p.id} project={p} index={offset + i} />
                ))}
            </div>
        </motion.div>
    );
}

export default function Portfolio() {
    let offset = 0;
    return (
        <section
            id="portfolio"
            className="mx-auto max-w-4xl px-6 py-24 sm:px-10 sm:py-32"
        >
            <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8C8B88]"
            >
                portfolio
            </motion.p>
            <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
                className="mt-6 font-serif text-3xl tracking-tight text-[#141413] sm:text-4xl"
            >
                a non-exhaustive list of past work
            </motion.h2>
            {PORTFOLIO.map((cat) => {
                const catOffset = offset;
                offset += cat.projects.length;
                return <Category key={cat.name} category={cat} offset={catOffset} />;
            })}
            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-12 text-sm text-[#8C8B88]"
            >
                The fuller record lives in{" "}
                <a
                    href={SITE.resume}
                    data-testid="portfolio-cv-link"
                    className="u-link font-medium text-[#141413] transition-colors duration-300 hover:text-[#C84B31]"
                >
                    my CV
                </a>
                .
            </motion.p>
        </section>
    );
}
